import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { ShieldX, ArrowLeft } from 'lucide-react';
import Login from './pages/Login';
import CompanySelector from './pages/CompanySelector';
import Dashboard from './pages/Dashboard';
import GreyOrdersList from './pages/GreyOrdersList';
import CreateGreyOrder from './pages/CreateGreyOrder';
import EditGreyOrder from './pages/EditGreyOrder';
import OrderDetails from './pages/OrderDetails';
import GreyBillsList from './pages/GreyBillsList';
import BillDetails from './pages/BillDetails';
import LRReportsList from './pages/LRReportsList';
import LRDetails from './pages/LRDetails';
import GreyReceiptsList from './pages/GreyReceiptsList';
import ReceiptDetails from './pages/ReceiptDetails';
import Masters from './pages/Masters';
import BulkSuppliers from './pages/BulkSuppliers';
import BulkQualities from './pages/BulkQualities';
import BulkGreyOrders from './pages/BulkGreyOrders';
import BulkGreyBills from './pages/BulkGreyBills';
import BulkFinishQualities from './pages/BulkFinishQualities';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import AuditLogs from './pages/AuditLogs';
import AppShell from './components/AppShell';
// Admin Pages
import AdminUsers from './pages/admin/Users';
import AdminRoles from './pages/admin/Roles';
import AdminAccessControl from './pages/admin/AccessControl';
import AdminSettings from './pages/admin/Settings';
import './App.css';

const ProtectedRoute = ({ children, requireCompany = true }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Company selection check — redirect to selector if user has multiple companies but none selected
  if (requireCompany) {
    try {
      const userCompanies = JSON.parse(localStorage.getItem('user_companies') || '[]');
      const activeCompanyId = localStorage.getItem('active_company_id');
      if (userCompanies.length > 1 && !activeCompanyId) {
        return <Navigate to="/select-company" replace />;
      }
      // User has no companies and is not admin — show selector with error state
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (userCompanies.length === 0 && storedUser.role !== 'admin' && !activeCompanyId) {
        return <Navigate to="/select-company" replace />;
      }
    } catch {
      // Invalid localStorage state — continue normally
    }
  }
  
  // Wrap authenticated children with Settings and Permissions providers
  return (
    <SettingsProvider>
      <PermissionsProvider>
        {children}
      </PermissionsProvider>
    </SettingsProvider>
  );
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

const ModuleGuard = ({ module, permission = 'view', children }) => {
  const { hasModulePermission, loading } = usePermissions();
  const navigate = useNavigate();
  
  if (loading) return null;
  
  if (!hasModulePermission(module, permission)) {
    return (
      <div data-testid="access-denied" className="flex flex-col items-center justify-center min-h-[60vh] p-8">
        <ShieldX className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
        <p className="text-slate-500 mb-6 text-center">You do not have permission to access this page.</p>
        <Button onClick={() => navigate('/dashboard')} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }
  
  return children;
};

const AdminGuard = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  if (user?.role !== 'admin') {
    return (
      <div data-testid="admin-access-denied" className="flex flex-col items-center justify-center min-h-[60vh] p-8">
        <ShieldX className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Admin Access Required</h2>
        <p className="text-slate-500 mb-6 text-center">Only administrators can access this section.</p>
        <Button onClick={() => navigate('/dashboard')} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/select-company" element={<ProtectedRoute requireCompany={false}><CompanySelector /></ProtectedRoute>} />
            
            <Route path="/" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<ModuleGuard module="dashboard"><Dashboard /></ModuleGuard>} />
              <Route path="grey-orders" element={<ModuleGuard module="grey_orders"><GreyOrdersList /></ModuleGuard>} />
              <Route path="grey-orders/create" element={<ModuleGuard module="grey_orders" permission="create"><CreateGreyOrder /></ModuleGuard>} />
              <Route path="grey-orders/:orderId" element={<ModuleGuard module="grey_orders"><OrderDetails /></ModuleGuard>} />
              <Route path="grey-orders/:orderId/edit" element={<ModuleGuard module="grey_orders" permission="edit"><EditGreyOrder /></ModuleGuard>} />
              <Route path="grey-bills" element={<ModuleGuard module="grey_bills"><GreyBillsList /></ModuleGuard>} />
              <Route path="grey-bills/*" element={<ModuleGuard module="grey_bills"><BillDetails /></ModuleGuard>} />
              <Route path="lr-reports" element={<ModuleGuard module="lr_reports"><LRReportsList /></ModuleGuard>} />
              <Route path="lr-reports/*" element={<ModuleGuard module="lr_reports"><LRDetails /></ModuleGuard>} />
              <Route path="grey-receipts" element={<ModuleGuard module="grey_receipts"><GreyReceiptsList /></ModuleGuard>} />
              <Route path="grey-receipts/*" element={<ModuleGuard module="grey_receipts"><ReceiptDetails /></ModuleGuard>} />
              <Route path="masters" element={<ModuleGuard module="masters"><Masters /></ModuleGuard>} />
              <Route path="bulk/suppliers" element={<ModuleGuard module="masters" permission="create"><BulkSuppliers /></ModuleGuard>} />
              <Route path="bulk/qualities" element={<ModuleGuard module="masters" permission="create"><BulkQualities /></ModuleGuard>} />
              <Route path="bulk/finish-qualities" element={<ModuleGuard module="masters" permission="create"><BulkFinishQualities /></ModuleGuard>} />
              <Route path="bulk/grey-orders" element={<ModuleGuard module="grey_orders" permission="create"><BulkGreyOrders /></ModuleGuard>} />
              <Route path="bulk/grey-bills/:orderId" element={<ModuleGuard module="grey_bills" permission="create"><BulkGreyBills /></ModuleGuard>} />
              <Route path="reports" element={<ModuleGuard module="reports"><Reports /></ModuleGuard>} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              {/* Admin Panel Routes */}
              <Route path="admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
              <Route path="admin/roles" element={<AdminGuard><AdminRoles /></AdminGuard>} />
              <Route path="admin/access-control" element={<AdminGuard><AdminAccessControl /></AdminGuard>} />
              <Route path="admin/settings" element={<AdminGuard><AdminSettings /></AdminGuard>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </CompanyProvider>
    </AuthProvider>
  );
}

export default App;
